import {
  Activity,
  ArrowUp,
  BarChart3,
  Bell,
  CalendarDays,
  Check,
  ChevronDown,
  Code2,
  Copy,
  Download,
  FileImage,
  FileSearch,
  Film,
  Fingerprint,
  Globe2,
  ImagePlus,
  Lightbulb,
  Loader2,
  MapPin,
  Menu,
  MessageSquare,
  MoreHorizontal,
  PenLine,
  Plus,
  RotateCcw,
  Search,
  Send,
  ShieldCheck,
  Sparkles,
  Square,
  Target,
  Upload,
  UserRound,
  X,
  Zap,
} from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import type { FormEvent, ReactNode } from 'react'
import { analyzeImage, askSove, consumeCredits, createVideoPlan, editImage, generateImage, getCredits, scanSecurity, searchMap, searchResearch } from './lib/api'
import type { CreditModel } from './lib/api'
import type { Message, Task, ToolId } from './types'

const toolList: Array<{
  id: ToolId
  label: string
  short: string
  description: string
  placeholder: string
  icon: typeof MessageSquare
  tone: string
}> = [
  { id: 'chat', label: 'AI 채팅', short: '대화', description: '생각을 정리하고 바로 실행해요', placeholder: '무엇을 도와드릴까요?', icon: MessageSquare, tone: 'blue' },
  { id: 'write', label: '글쓰기', short: '글', description: '초안부터 매끄럽게 다듬기', placeholder: '예: 신규 앱 출시 이메일 초안을 써줘', icon: PenLine, tone: 'purple' },
  { id: 'solve', label: '문제 풀이', short: '풀이', description: '과정까지 이해하는 답변', placeholder: '문제나 풀이가 필요한 내용을 입력하세요', icon: Target, tone: 'orange' },
  { id: 'code', label: '코드 작성', short: '코드', description: '아이디어를 작동하는 코드로', placeholder: '예: React로 검색 가능한 카드 UI를 만들어줘', icon: Code2, tone: 'green' },
  { id: 'research', label: '자료 검색', short: '검색', description: '출처·날짜·핵심 근거까지 모으기', placeholder: '예: 2026년 생성형 AI 시장의 주요 흐름과 공식 자료', icon: FileSearch, tone: 'cyan' },
  { id: 'image', label: '이미지 분석', short: '분석', description: '이미지를 보고 맥락을 읽기', placeholder: '이미지에 대해 무엇이 궁금한가요?', icon: FileImage, tone: 'pink' },
  { id: 'generate', label: '이미지 생성', short: '생성', description: '설명만으로 새로운 이미지 만들기', placeholder: '예: 비 오는 서울의 밤, 시네마틱한 포스터', icon: Sparkles, tone: 'purple' },
  { id: 'video', label: '영상 만들기', short: '영상', description: '스크립트·스토리보드·촬영 구성을 만들기', placeholder: '예: 30초 세로형 SOVE 소개 영상 만들어줘', icon: Film, tone: 'red' },
  { id: 'remove', label: '워터마크 제거', short: '편집', description: '권한 있는 이미지 편집 도구', placeholder: '편집 요청을 설명하세요', icon: ImagePlus, tone: 'yellow' },
  { id: 'money', label: '돈벌기 아이디어', short: '수익', description: '가능성을 실행 계획으로', placeholder: '예: 주말에 시작할 수 있는 디지털 상품 아이디어', icon: BarChart3, tone: 'lime' },
  { id: 'security', label: '보안 검사', short: '보안', description: '코드와 설정의 위험 신호 점검', placeholder: '검사할 코드나 설정을 붙여넣으세요', icon: ShieldCheck, tone: 'red' },
  { id: 'calendar', label: '일정 확인', short: '일정', description: '오늘의 할 일을 한눈에', placeholder: '새 일정을 입력하세요', icon: CalendarDays, tone: 'indigo' },
  { id: 'map', label: '지도 서비스', short: '지도', description: '장소와 위치를 빠르게 찾기', placeholder: '예: 서울 성수동 조용한 카페', icon: MapPin, tone: 'teal' },
]

const starterPrompts = [
  { label: '아이디어를 실행 계획으로', prompt: '지금 떠오른 아이디어를 오늘 바로 시작할 수 있는 3단계 계획으로 바꿔줘', icon: Zap },
  { label: '복잡한 내용 쉽게 설명', prompt: '이 내용을 초보자도 이해할 수 있도록 핵심 개념과 예시로 설명해줘', icon: Lightbulb },
  { label: '코드로 만들어보기', prompt: '작은 웹 도구 아이디어를 하나 제안하고 바로 실행 가능한 코드로 만들어줘', icon: Code2 },
]

const defaultTasks: Task[] = [
  { id: 't1', title: 'SOVE 소개 문구 다듬기', time: '10:00', tag: '집중', done: false },
  { id: 't2', title: '오후 미팅 준비', time: '14:30', tag: '미팅', done: false },
  { id: 't3', title: '이번 주 콘텐츠 아이디어 정리', time: '17:00', tag: '정리', done: true },
]

const welcome: Message = {
  id: 'welcome',
  role: 'assistant',
  time: '지금',
  content: '안녕하세요. 저는 **SOVE**예요.\n\n글쓰기부터 코드, 이미지, 검색, 일정까지 지금 필요한 일을 한 문장으로 시작해보세요. 왼쪽 도구를 고르면 제가 더 정확한 방식으로 도와드릴게요.',
}

function formatTime() {
  return new Intl.DateTimeFormat('ko-KR', { hour: '2-digit', minute: '2-digit' }).format(new Date())
}

function renderInline(text: string) {
  return text.split(/(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\(https?:\/\/[^)]+\))/g).map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) return <strong key={index}>{part.slice(2, -2)}</strong>
    if (part.startsWith('`') && part.endsWith('`')) return <code key={index}>{part.slice(1, -1)}</code>
    const link = /^\[([^\]]+)\]\((https?:\/\/[^)]+)\)$/.exec(part)
    if (link) return <a key={index} href={link[2]} target="_blank" rel="noreferrer">{link[1]}</a>
    return <span key={index}>{part}</span>
  })
}

function simpleMarkdown(text: string): ReactNode[] {
  const blocks: ReactNode[] = []
  const lines = text.split('\n')
  let inCode = false
  let codeLanguage = ''
  let codeLines: string[] = []
  lines.forEach((line, index) => {
    if (line.startsWith('```')) {
      if (inCode) {
        blocks.push(<pre className="markdown-code" key={`code-${index}`}><code>{codeLines.join('\n')}</code></pre>)
        codeLines = []
        inCode = false
        codeLanguage = ''
      } else {
        inCode = true
        codeLanguage = line.slice(3).trim()
      }
      return
    }
    if (inCode) {
      codeLines.push(line)
      return
    }
    if (!line.trim()) {
      blocks.push(<div className="markdown-spacer" key={`space-${index}`} />)
    } else if (/^#{1,3}\s/.test(line)) {
      blocks.push(<h4 className="markdown-heading" key={`heading-${index}`}>{renderInline(line.replace(/^#{1,3}\s/, ''))}</h4>)
    } else if (/^[-*]\s/.test(line)) {
      blocks.push(<div className="markdown-list" key={`list-${index}`}><span>•</span><span>{renderInline(line.replace(/^[-*]\s/, ''))}</span></div>)
    } else {
      blocks.push(<p className="markdown-paragraph" key={`paragraph-${index}`}>{renderInline(line)}</p>)
    }
  })
  if (inCode && codeLines.length) blocks.push(<pre className="markdown-code" data-language={codeLanguage} key="code-end"><code>{codeLines.join('\n')}</code></pre>)
  return blocks
}

function demoReply(tool: ToolId, input: string) {
  const replies: Record<ToolId, string> = {
    chat: `요청을 받았어요. “${input}”을 기준으로 지금 할 수 있는 다음 행동을 정리하면, 먼저 목표를 한 문장으로 좁히고 필요한 자료를 모은 뒤 작은 결과물을 만드는 흐름이 가장 빨라요.\n\n원하시면 이 대화를 글쓰기·코드·검색 모드로 이어갈 수 있어요.`,
    write: `좋아요. 아래처럼 바로 사용할 수 있는 초안으로 시작해볼게요.\n\n**제목**\n${input}\n\n**핵심 메시지**\n독자가 지금 알아야 할 내용을 한 문장으로 정리하고, 다음 행동을 자연스럽게 제안합니다.\n\n원하는 말투(친근하게, 전문적으로, 짧게)를 알려주면 한 번 더 다듬어드릴게요.`,
    solve: `문제를 작은 단계로 나눠볼게요.\n\n**1. 주어진 정보 확인**\n입력한 조건에서 핵심 값과 목표를 분리합니다.\n\n**2. 풀이 전략**\n알고 있는 공식이나 규칙을 적용하고 중간 결과를 검산합니다.\n\n**3. 결론**\n${input}에 대한 최종 답은 조건을 다시 대입해 확인하는 것이 좋아요. 문제 이미지를 첨부하면 더 구체적으로 풀 수 있어요.`,
    code: `요청을 구현 단위로 쪼개면 좋아요.\n\n\`\`\`tsx\nexport function SoveStarter() {\n  return <section aria-label="SOVE starter">\n    <h2>작은 결과부터 시작해요</h2>\n    <button type="button">실행하기</button>\n  </section>\n}\n\`\`\`\n\n다음 단계에서는 입력 상태, API 연결, 오류 처리를 붙이면 실제 기능으로 확장할 수 있어요.`,
    money: `가능성이 있는 방향을 세 가지로 좁혔어요.\n\n**1. 반복 업무 템플릿 판매** — 초기 비용이 낮고 검증이 빠릅니다.\n**2. 소규모 사업자용 콘텐츠 패키지** — 특정 업종을 정하면 단가를 만들기 쉽습니다.\n**3. 마이크로 도구 제작** — 작은 불편을 해결하는 유료 웹 도구로 확장할 수 있어요.\n\n가장 중요한 건 첫 주에 실제 고객 3명에게 문제를 확인하는 것입니다.`,
    security: `기본 정적 점검을 시작했어요. 입력한 내용에서 **비밀키 노출**, **위험한 동적 실행**, **검증되지 않은 사용자 입력**을 먼저 확인하는 방식입니다.\n\n이 결과는 보안 보조 리뷰이며, 실제 침투 테스트나 전문 보안 감사는 아닙니다.`,
    image: '이미지를 받으면 보이는 요소, 텍스트, 구조, 분위기를 나눠서 분석할 수 있어요. 먼저 파일을 첨부해 주세요.',
    generate: '원하는 장면, 분위기, 색감, 비율을 설명하면 새로운 이미지를 생성할 수 있어요.',
    video: '영상의 주제, 길이, 화면 비율, 분위기를 설명하면 스크립트와 장면별 스토리보드를 만들 수 있어요.',
    remove: '편집할 이미지를 첨부한 뒤 원하는 영역을 설명해 주세요. 원본에 대한 편집 권한이 있는 경우에만 사용하고, 결과는 생성형 편집 특성상 원본과 픽셀 단위로 같지 않을 수 있어요.',
    research: '검색 연결 상태를 확인하고 있어요. 실제 출처가 확인된 경우에만 링크와 함께 요약하고, 그렇지 않으면 AI 조사 초안으로 명확히 표시할게요.',
    calendar: '일정 모드에서는 입력한 내용을 오늘의 할 일로 저장할 수 있어요. 외부 캘린더와 동기화하려면 연결이 필요합니다.',
    map: '장소 검색을 준비했어요. 주소나 장소 이름을 입력하면 지도 검색 결과를 보여드릴게요.',
  }
  return replies[tool]
}

function App() {
  const [selectedTool, setSelectedTool] = useState<ToolId>('chat')
  const [messages, setMessages] = useState<Message[]>(() => {
    try {
      const saved = localStorage.getItem('sove-messages')
      return saved ? JSON.parse(saved) : [welcome]
    } catch {
      return [welcome]
    }
  })
  const [tasks, setTasks] = useState<Task[]>(() => {
    try {
      const saved = localStorage.getItem('sove-tasks')
      return saved ? JSON.parse(saved) : defaultTasks
    } catch {
      return defaultTasks
    }
  })
  const [input, setInput] = useState('')
  const [isThinking, setIsThinking] = useState(false)
  const [copiedId, setCopiedId] = useState<string | null>(null)
  const [mobileNavOpen, setMobileNavOpen] = useState(false)
  const [selectedFile, setSelectedFile] = useState<{ name: string; preview: string } | null>(null)
  const [notice, setNotice] = useState('')
  const [resultPanel, setResultPanel] = useState<{ kind: 'research' | 'map' | 'security' | 'image' | 'video'; title: string; body: string; live?: boolean; imageUrl?: string; videoUrl?: string; mapUrl?: string; sources?: Array<{ title: string; url: string; snippet?: string; provider?: string; date?: string }> } | null>(null)
  const [permissionConfirmed, setPermissionConfirmed] = useState(false)
  const [lastFailedRequest, setLastFailedRequest] = useState<{ tool: ToolId; text: string; file: { name: string; preview: string } | null } | null>(null)
  const [authOpen, setAuthOpen] = useState(false)
  const [authMode, setAuthMode] = useState<'signup' | 'login'>('signup')
  const [authUser, setAuthUser] = useState<{ nickname: string; guest: boolean } | null>(null)
  const [authNickname, setAuthNickname] = useState('')
  const [authPassword, setAuthPassword] = useState('')
  const [authChallenge, setAuthChallenge] = useState('')
  const [authNotice, setAuthNotice] = useState('')
  const [showDisclaimer, setShowDisclaimer] = useState(true)
  const [creditModel, setCreditModel] = useState<CreditModel>('smart')
  const [creditState, setCreditState] = useState<{ limit: number; used: number; remaining: number; costs: Record<CreditModel, number>; premiumDiscount: number } | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const abortRef = useRef<AbortController | null>(null)
  const disclaimerStartY = useRef<number | null>(null)

  const currentTool = useMemo(() => toolList.find((tool) => tool.id === selectedTool) ?? toolList[0], [selectedTool])
  const incompleteTasks = tasks.filter((task) => !task.done).length

  const startNewChat = () => {
    setMessages([welcome])
    setInput('')
    setSelectedTool('chat')
    setSelectedFile(null)
    setResultPanel(null)
    setNotice('')
    setPermissionConfirmed(false)
  }

  useEffect(() => {
    localStorage.setItem('sove-messages', JSON.stringify(messages))
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  useEffect(() => {
    localStorage.setItem('sove-tasks', JSON.stringify(tasks))
  }, [tasks])

  useEffect(() => {
    fetch('/api/auth/me').then((response) => response.json()).then((payload) => setAuthUser(payload.user ?? null)).catch(() => undefined)
  }, [])

  useEffect(() => {
    if (!authUser || authUser.guest) setCreditModel('default')
  }, [authUser])

  useEffect(() => {
    const refreshCredits = () => getCredits().then(setCreditState).catch(() => undefined)
    refreshCredits()
    const timer = window.setInterval(refreshCredits, 5000)
    return () => window.clearInterval(timer)
  }, [authUser])

  const submitAuth = async (event: FormEvent) => {
    event.preventDefault()
    setAuthNotice('')
    try {
      const response = await fetch(`/api/auth/${authMode}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ nickname: authNickname, password: authPassword, challenge: authChallenge }) })
      const payload = await response.json()
      if (!response.ok) throw new Error(payload.error || '인증을 처리하지 못했어요.')
      setAuthUser(payload.user)
      setAuthOpen(false)
      setAuthPassword('')
      setAuthChallenge('')
    } catch (error) {
      setAuthNotice(error instanceof Error ? error.message : '인증을 처리하지 못했어요.')
    }
  }

  const continueAsGuest = async () => {
    const response = await fetch('/api/auth/guest', { method: 'POST' })
    const payload = await response.json()
    setAuthUser(payload.user)
    setAuthOpen(false)
  }

  const logout = async () => {
    await fetch('/api/auth/logout', { method: 'POST' })
    setAuthUser(null)
  }

  const selectCreditModel = (model: CreditModel) => {
    if (model !== 'default' && (!authUser || authUser.guest)) {
      setCreditModel('default')
      setAuthMode('signup')
      setAuthNotice('Smart와 Advanced Smart는 회원가입 후 사용할 수 있어요.')
      setAuthOpen(true)
      return
    }
    setCreditModel(model)
  }

  const selectTool = (id: ToolId) => {
    setSelectedTool(id)
    setNotice('')
    setResultPanel(null)
    setPermissionConfirmed(false)
    setMobileNavOpen(false)
  }

  const addAssistant = (content: string, tool = selectedTool) => {
    setMessages((previous) => [...previous, { id: crypto.randomUUID(), role: 'assistant', content, time: formatTime(), tool }])
  }

  const submit = async () => {
    if (isThinking || (!input.trim() && !((selectedTool === 'image' || selectedTool === 'chat') && selectedFile))) return
    if ((selectedTool === 'image' || selectedTool === 'remove') && !selectedFile) {
      setNotice('먼저 이미지를 첨부해 주세요.')
      return
    }
    if (selectedTool === 'remove' && !permissionConfirmed) {
      setNotice('편집 권한이 있는 이미지임을 확인해 주세요.')
      return
    }
    try {
      const charged = await consumeCredits(creditModel)
      setCreditState((previous) => previous ? { ...previous, ...charged } : charged)
    } catch (error) {
      setNotice(error instanceof Error ? error.message : '크레딧을 확인하지 못했어요.')
      return
    }
    const text = input.trim() || (selectedTool === 'image' ? '이 이미지를 분석해줘' : '')
    const userMessage: Message = { id: crypto.randomUUID(), role: 'user', content: text, time: formatTime(), tool: selectedTool }
    setMessages((previous) => [...previous, userMessage])
    setInput('')
    setNotice('')
    setLastFailedRequest(null)
    setIsThinking(true)
    abortRef.current = new AbortController()

    try {
      if (selectedTool === 'calendar') {
        const task: Task = { id: crypto.randomUUID(), title: text, time: formatTime(), tag: '새 일정', done: false }
        setTasks((previous) => [task, ...previous])
        addAssistant(`일정을 추가했어요. **${text}**\n\n오늘 일정 패널에서 상태를 확인할 수 있습니다.`)
      } else if (selectedTool === 'security') {
        const result = await scanSecurity(text)
        setResultPanel({ kind: 'security', title: '보안 검사 결과', body: result.content })
        addAssistant(result.content)
      } else if (selectedTool === 'research') {
        const result = await searchResearch(text)
        setResultPanel({ kind: 'research', title: result.live ? '출처 기반 검색' : 'AI 조사 초안', body: result.content, live: result.live, sources: result.sources })
        addAssistant(result.content)
      } else if (selectedTool === 'map') {
        const result = await searchMap(text)
        setResultPanel({ kind: 'map', title: result.configured ? '지도 검색 결과' : '지도 연결 준비', body: result.content, mapUrl: result.mapUrl })
        addAssistant(result.content)
      } else if (selectedTool === 'image' || (selectedTool === 'chat' && selectedFile)) {
        const result = await analyzeImage(selectedFile?.name ?? '업로드 이미지', selectedFile?.preview ?? '')
        setResultPanel({ kind: 'image', title: '이미지 분석', body: result.content })
        addAssistant(`**이미지 분석을 완료했어요.**\n\n${result.content}`)
      } else if (selectedTool === 'generate') {
        const result = await generateImage(text)
        setResultPanel({ kind: 'image', title: '이미지 생성 결과', body: result.content, imageUrl: result.imageUrl })
        addAssistant(result.content)
      } else if (selectedTool === 'video') {
        const result = await createVideoPlan(text)
        setResultPanel({ kind: 'video', title: '영상 결과', body: result.content, videoUrl: result.videoUrl })
        addAssistant(result.content)
      } else if (selectedTool === 'remove') {
        const result = await editImage(selectedFile?.name ?? '업로드 이미지', selectedFile?.preview ?? '', text)
        setResultPanel({ kind: 'image', title: '이미지 편집', body: result.content, imageUrl: result.imageUrl })
        addAssistant(result.content)
      } else {
        const result = await askSove({ mode: selectedTool, message: text, history: messages.slice(-8), model: creditModel }, abortRef.current.signal)
        addAssistant(result.content)
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        setNotice('요청을 중지했어요. 입력 내용은 다시 작성할 수 있습니다.')
      } else {
        const message = error instanceof Error ? error.message : '알 수 없는 연결 오류가 발생했어요.'
        setLastFailedRequest({ tool: selectedTool, text, file: selectedFile })
        setNotice('요청이 실패했어요. 아래에서 다시 시도할 수 있어요.')
        addAssistant(`요청을 처리하지 못했어요.\n\n${message}\n\n아래의 다시 시도로 같은 요청을 재전송할 수 있어요.`)
      }
    } finally {
      setIsThinking(false)
      abortRef.current = null
    }
  }

  const cancelRequest = () => {
    abortRef.current?.abort()
    setIsThinking(false)
  }

  const retryLastRequest = () => {
    if (!lastFailedRequest) return
    setSelectedTool(lastFailedRequest.tool)
    setInput(lastFailedRequest.text)
    setSelectedFile(lastFailedRequest.file)
    window.setTimeout(() => submit(), 0)
  }

  const copyMessage = async (message: Message) => {
    await navigator.clipboard?.writeText(message.content)
    setCopiedId(message.id)
    window.setTimeout(() => setCopiedId(null), 1600)
  }

  const handleFile = (file?: File) => {
    if (!file) return
    if (!file.type.startsWith('image/')) {
      setNotice('PNG, JPG, WEBP 같은 이미지 파일을 첨부해 주세요.')
      return
    }
    if (file.size > 8 * 1024 * 1024) {
      setNotice('이미지는 8MB 이하로 첨부해 주세요.')
      return
    }
    const reader = new FileReader()
    reader.onload = () => { const preview = String(reader.result || ''); if (!preview.startsWith('data:image/')) { setNotice('이미지 미리보기를 읽지 못했어요. 다른 파일을 선택해 주세요.'); return }; setSelectedFile({ name: file.name, preview }); setPermissionConfirmed(false) }
    reader.onerror = () => setNotice('이미지를 읽지 못했어요. 파일을 다시 선택해 주세요.')
    reader.readAsDataURL(file)
    setNotice('')
  }

  const toggleTask = (id: string) => setTasks((previous) => previous.map((task) => task.id === id ? { ...task, done: !task.done } : task))

  return (
    <div className="app-shell">
      <div className="ambient ambient-one" />
      <div className="ambient ambient-two" />
      <header className="mobile-header">
        <button className="icon-button" onClick={() => setMobileNavOpen(true)} aria-label="도구 메뉴 열기"><Menu size={19} /></button>
        <div className="mobile-brand"><span className="brand-mark">S</span><strong>SOVE</strong></div>
        <span className="mobile-header-spacer" aria-hidden="true" />
      </header>
      <aside className={`sidebar ${mobileNavOpen ? 'is-open' : ''}`}>
        <div className="sidebar-top">
          <div className="brand-lockup"><span className="brand-mark">S</span><div><strong>SOVE</strong><span>AI WORKSPACE</span></div></div>
          <button className="sidebar-close icon-button" onClick={() => setMobileNavOpen(false)} aria-label="메뉴 닫기"><X size={18} /></button>
        </div>
        <div className="workspace-switch"><div className="avatar">{(authUser?.nickname ?? '게스트').slice(0, 1).toUpperCase()}</div><div><b>{authUser?.nickname ?? '게스트'}'s space</b><span>{authUser?.guest ? '게스트 워크스페이스' : '개인 워크스페이스'}</span></div><ChevronDown size={14} /></div>
        <div className="side-label">WORKSPACE</div>
        <button className="new-chat" onClick={startNewChat}><Plus size={17} /> 새 채팅</button>
        <nav className="primary-nav"><button className="nav-item active"><MessageSquare size={17} /> 대화 <span className="nav-count">{messages.length - 1}</span></button><button className="nav-item"><Activity size={17} /> 활동 기록</button><button className="nav-item"><Fingerprint size={17} /> 내 라이브러리</button></nav>
        <div className="side-label tool-label">TOOLS <span>13</span></div>
        <div className="tool-nav">{toolList.map((tool) => { const Icon = tool.icon; return <button key={tool.id} className={`tool-nav-item ${selectedTool === tool.id ? 'selected' : ''}`} onClick={() => selectTool(tool.id)}><span className={`tool-icon ${tool.tone}`}><Icon size={15} /></span><span>{tool.label}</span></button> })}</div>
        <div className="sidebar-footer"><div className="usage-head"><span>이번 달 사용량</span><b>{creditState ? Math.round((creditState.used / creditState.limit) * 100) : 0}%</b></div><div className="usage-bar"><span style={{ width: `${creditState ? Math.min(100, (creditState.used / creditState.limit) * 100) : 0}%` }} /></div><small>{creditState?.remaining.toLocaleString() ?? '30,000'} 크레딧 남음</small><div className="profile-row"><div className="profile-avatar">{(authUser?.nickname ?? '게스트').slice(0, 2).toUpperCase()}</div><div><b>{authUser?.nickname ?? '게스트'}</b><span>계정 확인</span></div><MoreHorizontal size={17} /></div></div>
      </aside>
      {mobileNavOpen && <button className="mobile-backdrop" onClick={() => setMobileNavOpen(false)} aria-label="메뉴 닫기" />}
      <main className="main-content">
        <div className="topbar"><div className="breadcrumb"><span>워크스페이스</span><span>/</span><b>{currentTool.label}</b></div><div className="topbar-actions"><span className="credit-pill">크레딧 {creditState?.remaining.toLocaleString() ?? '30,000'}/30,000</span><button className="top-new-chat" onClick={startNewChat}><Plus size={14} /> 새 채팅</button><div className="status-pill"><span className="status-dot" /> 모든 시스템 정상</div><button className="icon-button" aria-label="알림"><Bell size={18} /></button><button className="account-button" onClick={() => setAuthOpen(true)}><UserRound size={14} /> {authUser?.nickname ?? '회원가입'}</button></div></div>
        <nav className="tool-tabs" aria-label="SOVE 기능 탭" role="tablist">{toolList.map((tool) => { const Icon = tool.icon; return <button key={tool.id} role="tab" aria-selected={selectedTool === tool.id} className={selectedTool === tool.id ? 'active' : ''} onClick={() => selectTool(tool.id)}><Icon size={15} /><span>{tool.label}</span></button> })}</nav>
        <div className={`content-grid ${selectedTool === 'chat' ? '' : 'tool-focused'}`}>
          <section className="chat-column">
            <div className="hero-row"><div><div className="eyebrow"><Sparkles size={13} /> YOUR AI COMMAND CENTER</div><h1>{selectedTool === 'chat' ? '무엇이든, <em>시작해보세요.</em>' : `${currentTool.label}, 바로 시작해요.`}</h1><p>{selectedTool === 'chat' ? '복잡한 일도 한 문장으로 시작하면 됩니다.' : currentTool.description}</p></div><div className="date-chip"><span>오늘</span><b>{new Intl.DateTimeFormat('ko-KR', { month: 'short', day: 'numeric', weekday: 'short' }).format(new Date())}</b></div></div>
            {selectedTool === 'chat' && <div className="starter-grid">{starterPrompts.map((starter) => { const Icon = starter.icon; return <button key={starter.label} className="starter-card" onClick={() => { setInput(starter.prompt); setSelectedTool('chat') }}><span className="starter-icon"><Icon size={17} /></span><span><b>{starter.label}</b><small>{starter.prompt}</small></span><ArrowUp size={15} className="starter-arrow" /></button> })}</div>}
            {selectedFile && <div className="attachment-chip"><img src={selectedFile.preview} alt="첨부 미리보기" /><span>{selectedFile.name}</span><button onClick={() => setSelectedFile(null)} aria-label="첨부 삭제"><X size={14} /></button></div>}
            <div className="conversation">{messages.map((message) => <article key={message.id} className={`message ${message.role}`}><div className="message-avatar">{message.role === 'assistant' ? <span className="mini-mark">S</span> : 'JS'}</div><div className="message-body"><div className="message-meta"><b>{message.role === 'assistant' ? 'SOVE' : '나'}</b><span>{message.time}</span>{message.tool && message.role === 'user' && <span className="message-tool">{toolList.find((tool) => tool.id === message.tool)?.short}</span>}</div><div className="message-content">{simpleMarkdown(message.content)}</div>{message.role === 'assistant' && <div className="message-actions"><button onClick={() => copyMessage(message)}>{copiedId === message.id ? <Check size={13} /> : <Copy size={13} />} {copiedId === message.id ? '복사됨' : '복사'}</button><button onClick={() => setInput(message.content)}>이어쓰기</button></div>}</div></article>)}{isThinking && <article className="message assistant"><div className="message-avatar"><span className="mini-mark">S</span></div><div className="message-body"><div className="message-meta"><b>SOVE</b><span>처리 중</span></div><div className="thinking"><span /><span /><span /><em>{currentTool.label}을 처리하고 있어요</em></div></div></article>}<div ref={messagesEndRef} /></div>
            {selectedTool !== 'chat' && resultPanel && <div className="focused-output"><div className="focused-output-head"><span>{resultPanel.title}</span><span>결과</span></div><p>{resultPanel.body}</p>{resultPanel.kind === 'research' && resultPanel.sources?.map((source) => <a key={source.url} href={source.url} target="_blank" rel="noreferrer"><Search size={13} />{source.title}</a>)}{resultPanel.kind === 'map' && resultPanel.mapUrl && <img src={resultPanel.mapUrl} alt="지도 미리보기" />}{resultPanel.kind === 'video' && resultPanel.videoUrl && <video className="focused-video" src={resultPanel.videoUrl} controls playsInline preload="metadata" />}{resultPanel.kind === 'image' && (selectedFile || resultPanel.imageUrl) && <><img src={resultPanel.imageUrl ?? selectedFile?.preview} alt="작업 결과" />{resultPanel.imageUrl && <a href={resultPanel.imageUrl} download={`sove-${selectedFile?.name ?? 'generated-image.png'}`}><Download size={13} /> 결과 다운로드</a>}</>}</div>}
            <div className="composer-wrap"><div className={`composer ${notice ? 'has-error' : ''}`}><div className="composer-tools"><button className="attach-button" type="button" onClick={() => { if (fileRef.current) { fileRef.current.value = ''; fileRef.current.click() } }} aria-label="파일 첨부"><Upload size={18} /></button><input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" hidden onChange={(event) => handleFile(event.target.files?.[0])} /><span className="composer-mode"><span className={`tool-icon small ${currentTool.tone}`}><currentTool.icon size={13} /></span>{currentTool.label}<ChevronDown size={13} /></span><select className="model-select" value={creditModel} onChange={(event) => selectCreditModel(event.target.value as CreditModel)} aria-label="모델 선택"><option value="default">기본 · 0 · 간단</option><option value="smart">Smart · 100 · 균형</option><option value="advanced">Advanced Smart · 150 · 심층</option></select></div>{selectedTool === 'remove' && selectedFile && <label className="permission-check"><input type="checkbox" checked={permissionConfirmed} onChange={(event) => setPermissionConfirmed(event.target.checked)} /> 이 이미지에 대한 편집 권한이 있습니다.</label>}<textarea value={input} onChange={(event) => setInput(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter' && !event.shiftKey) { event.preventDefault(); submit() } }} placeholder={currentTool.placeholder} rows={2} /><div className="composer-bottom"><span>Enter로 전송 · Shift + Enter로 줄바꿈</span><button className="send-button" onClick={isThinking ? cancelRequest : submit} disabled={!isThinking && !input.trim() && !((selectedTool === 'image' || selectedTool === 'chat') && selectedFile)} aria-label={isThinking ? '요청 중지' : '전송'}>{isThinking ? <Square size={15} fill="currentColor" /> : <Send size={16} />}</button></div></div>{notice && <div className="composer-notice"><span>{notice}</span>{lastFailedRequest && <button className="retry-button" onClick={retryLastRequest}><RotateCcw size={12} /> 다시 시도</button>}<button onClick={() => setNotice('')}><X size={13} /></button></div>}<div className="composer-disclaimer"><span><ShieldCheck size={12} /> SOVE는 실수를 할 수 있어요. 중요한 정보는 확인해 주세요.</span><span>모델: {creditModel === 'advanced' ? 'Advanced Smart' : creditModel === 'smart' ? 'SOVE Smart' : 'SOVE Default'} · {creditState?.premiumDiscount ?? 200} 할인 <span className="green-dot" /></span></div></div>
          </section>
          {selectedTool === 'chat' && <aside className="context-panel">
            <div className="panel-header"><div><span className="eyebrow small">LIVE CONTEXT</span><h2>오늘의 보드</h2></div><button className="icon-button ghost" aria-label="새로고침"><RotateCcw size={16} /></button></div>
            <div className="focus-card"><div className="focus-top"><span className="focus-label"><span className="pulse-dot" /> TODAY'S FOCUS</span><MoreHorizontal size={16} /></div><h3>{incompleteTasks > 0 ? `남은 일 ${incompleteTasks}개를<br /><em>가볍게 정리해볼까요?</em>` : <>오늘 할 일이<br /><em>모두 정리됐어요.</em></>}</h3><div className="focus-progress"><div><span>오늘의 진행률</span><b>{Math.round(((tasks.length - incompleteTasks) / tasks.length) * 100)}%</b></div><div className="progress-line"><span style={{ width: `${Math.round(((tasks.length - incompleteTasks) / tasks.length) * 100)}%` }} /></div></div></div>
            <div className="panel-section"><div className="section-title"><div><span className="eyebrow small">UP NEXT</span><h3>오늘의 일정 <span>{incompleteTasks}</span></h3></div><button onClick={() => selectTool('calendar')} className="text-button">전체 보기 <ArrowUp size={13} /></button></div><div className="task-list">{tasks.slice(0, 3).map((task) => <button className={`task-item ${task.done ? 'done' : ''}`} key={task.id} onClick={() => toggleTask(task.id)}><span className="task-check">{task.done && <Check size={12} />}</span><span className="task-info"><b>{task.title}</b><small>{task.time} · {task.tag}</small></span><MoreHorizontal size={16} /></button>)}</div></div>
            <div className="panel-section result-section"><div className="section-title"><div><span className="eyebrow small">TOOL OUTPUT</span><h3>{resultPanel?.title ?? '최근 결과'}</h3></div><span className="live-label"><span className="green-dot" /> LIVE</span></div>{resultPanel ? <div className="result-card"><div className="result-badge">{resultPanel.live ? <Globe2 size={13} /> : resultPanel.kind === 'map' ? <MapPin size={13} /> : resultPanel.kind === 'video' ? <Film size={13} /> : <Sparkles size={13} />}{resultPanel.live ? '출처 확인됨' : resultPanel.kind === 'map' ? '지도 검색' : resultPanel.kind === 'video' ? '영상 제작안' : 'SOVE 작업 결과'}</div><p>{resultPanel.body}</p>{resultPanel.kind === 'research' && (resultPanel.sources?.length ? <div className="source-list">{resultPanel.sources.map((source) => <a key={source.url} href={source.url} target="_blank" rel="noreferrer"><Search size={12} /><span><b>{source.title}</b><small className="source-meta">{source.provider ?? '원문'}{source.date ? ` · ${source.date}` : ''}</small>{source.snippet && <small>{source.snippet}</small>}</span></a>)}</div> : <div className="source-placeholder"><span className="source-icon"><Search size={13} /></span><span><b>검색 연결을 준비 중이에요</b><small>연결 후 실제 출처 카드가 표시됩니다</small></span></div>)}{resultPanel.kind === 'map' && resultPanel.mapUrl && <img className="map-preview" src={resultPanel.mapUrl} alt="장소 지도 미리보기" />}{resultPanel.kind === 'video' && resultPanel.videoUrl && <><video className="result-video" src={resultPanel.videoUrl} controls playsInline preload="metadata" /><a className="download-button" href={resultPanel.videoUrl} download="sove-video.mp4"><Download size={13} /> 영상 다운로드</a></>}{resultPanel.kind === 'image' && (selectedFile || resultPanel.imageUrl) && <><img className="result-preview" src={resultPanel.imageUrl ?? selectedFile?.preview} alt="작업 결과 이미지" />{resultPanel.imageUrl && <a className="download-button" href={resultPanel.imageUrl} download={`sove-${selectedFile?.name ?? 'generated-image.png'}`}><Download size={13} /> 결과 다운로드</a>}</>}</div> : <div className="empty-result"><div className="empty-icon"><Activity size={18} /></div><b>작업 결과가 여기에 표시돼요</b><span>검색, 이미지, 보안 도구를 사용하면<br />관련 컨텍스트를 모아볼 수 있어요.</span></div>}</div>
            <div className="connection-card"><div className="connection-icon"><Globe2 size={17} /></div><div><b>외부 연결</b><span>Google 캘린더 · 지도 · 연결되지 않음</span></div><button onClick={() => setNotice('외부 캘린더 동기화는 연결 후 사용할 수 있어요.')}>연결 필요</button></div>
          </aside>}
        </div>
      </main>
      {showDisclaimer && <div className="legal-disclaimer" role="note" tabIndex={0} onPointerDown={(event) => { disclaimerStartY.current = event.clientY; event.currentTarget.setPointerCapture(event.pointerId) }} onPointerUp={(event) => { if (disclaimerStartY.current !== null && disclaimerStartY.current - event.clientY > 35) setShowDisclaimer(false); disclaimerStartY.current = null }} onKeyDown={(event) => { if (event.key === 'Escape' || event.key === 'Enter') setShowDisclaimer(false) }}>SOVE AI는 실수할 수 있습니다. 잘못된 정보를 제공할 수 있고, 그 정보 때문에 발생한 법적 문제 및 기타 문제에 대해 책임지지 않습니다.<span className="disclaimer-hint"> 위로 밀어 닫기</span></div>}
      <div className="mobile-account-footer">{authUser ? <><span><UserRound size={15} /> {authUser.nickname}{authUser.guest ? ' · 게스트' : ''}</span><button onClick={logout}>로그아웃</button></> : <><span>더 많은 기능을 사용해 보세요</span><div><button onClick={() => { setAuthMode('signup'); setAuthOpen(true) }}>회원가입</button><button onClick={continueAsGuest}>게스트로 계속</button></div></>}</div>
      <div className="mobile-toolbar">{toolList.slice(0, 5).map((tool) => { const Icon = tool.icon; return <button key={tool.id} className={selectedTool === tool.id ? 'active' : ''} onClick={() => selectTool(tool.id)}><Icon size={17} /><span>{tool.short}</span></button> })}</div>
      {authOpen && <div className="auth-backdrop" role="presentation" onClick={() => setAuthOpen(false)}><section className="auth-card" role="dialog" aria-modal="true" aria-labelledby="auth-title" onClick={(event) => event.stopPropagation()}><button className="auth-close" onClick={() => setAuthOpen(false)} aria-label="닫기"><X size={17} /></button>{authUser ? <><div className="auth-mark"><UserRound size={19} /></div><h2 id="auth-title">{authUser.nickname}님</h2><p>{authUser.guest ? '게스트로 이용 중입니다.' : '현재 계정으로 이용 중입니다.'}</p><div className="account-summary"><div><span>이번 달 잔여</span><b>{creditState?.remaining.toLocaleString() ?? '30,000'} 크레딧</b></div><div><span>월 한도</span><b>{creditState?.limit.toLocaleString() ?? '30,000'} 크레딧</b></div><div><span>프리미엄 혜택</span><b>200 크레딧 할인</b></div></div><button className="auth-submit" onClick={() => setAuthOpen(false)}>계속하기</button><button className="auth-logout" onClick={logout}>로그아웃</button></> : <><div className="auth-mark"><UserRound size={19} /></div><h2 id="auth-title">{authMode === 'signup' ? 'SOVE 시작하기' : 'SOVE 로그인'}</h2><p>{authMode === 'signup' ? '닉네임과 비밀번호만으로 가입할 수 있어요.' : '가입한 닉네임으로 다시 들어오세요.'}</p><form onSubmit={submitAuth}><label>닉네임<input value={authNickname} onChange={(event) => setAuthNickname(event.target.value)} placeholder="예: jisoo" autoComplete="username" required /></label><label>비밀번호<input type="password" value={authPassword} onChange={(event) => setAuthPassword(event.target.value)} placeholder="8자 이상" autoComplete={authMode === 'signup' ? 'new-password' : 'current-password'} required /></label>{authMode === 'signup' && <label>봇 방지 확인 <span className="challenge-hint">3569a · 3454 · 3685jk · heue 중 하나</span><input value={authChallenge} onChange={(event) => setAuthChallenge(event.target.value)} placeholder="문구를 정확히 입력" required /></label>}{authNotice && <div className="auth-notice">{authNotice}</div>}<button className="auth-submit" type="submit">{authMode === 'signup' ? '회원가입' : '로그인'}</button></form><button className="guest-button" onClick={continueAsGuest}>게스트로 계속하기</button><button className="auth-switch" onClick={() => { setAuthMode(authMode === 'signup' ? 'login' : 'signup'); setAuthNotice('') }}>{authMode === 'signup' ? '이미 계정이 있나요? 로그인' : '계정이 없나요? 회원가입'}</button><small className="auth-footnote">게스트 이용은 브라우저 세션에서만 유지됩니다.</small></>}</section></div>}
    </div>
  )
}

export default App
